import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Map, MapPin, Navigation, Copy } from 'lucide-react';
import { toast } from 'sonner';

/**
 * DirectionsSheet
 * Bottom-sheet picker that lets the user choose a maps app for directions.
 *
 * Location fallback hierarchy (shared with the Get Directions button):
 *   1. {lat,lng}              when both numbers are present
 *   2. game.address            full street address
 *   3. game.location_name (+ city)   venue name with optional city
 *
 * If none of these exist, the parent should not render the trigger button,
 * but we still guard here to render nothing.
 */
const DirectionsSheet = ({ open, onOpenChange, game }) => {
  if (!game) return null;

  const lat = game.latitude;
  const lng = game.longitude;
  const hasCoords = typeof lat === 'number' && typeof lng === 'number';

  // Query used for maps URLs (coords preferred — most accurate)
  let mapsQuery = '';
  if (hasCoords) mapsQuery = `${lat},${lng}`;
  else if (game.address) mapsQuery = game.address;
  else if (game.location_name) mapsQuery = game.city ? `${game.location_name} ${game.city}` : game.location_name;

  // Human-readable text used for Copy Address (address preferred — most useful to a human)
  let copyText = '';
  if (game.address) copyText = game.address;
  else if (game.location_name) copyText = game.city ? `${game.location_name}, ${game.city}` : game.location_name;
  else if (hasCoords) copyText = `${lat},${lng}`;

  if (!mapsQuery) return null;

  const encoded = encodeURIComponent(mapsQuery);
  const appleUrl = `https://maps.apple.com/?q=${encoded}`;
  const googleUrl = `https://www.google.com/maps/search/?api=1&query=${encoded}`;
  const wazeUrl = `https://waze.com/ul?q=${encoded}&navigate=yes`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      toast.success('Address copied');
    } catch {
      // Fallback for older browsers / non-secure contexts
      const ta = document.createElement('textarea');
      ta.value = copyText;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        toast.success('Address copied');
      } catch {
        toast.error('Could not copy address');
      }
      document.body.removeChild(ta);
    }
    onOpenChange(false);
  };

  const optionClass =
    'w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-zinc-50 hover:bg-zinc-100 active:bg-zinc-200 active:scale-[0.99] transition-all text-left';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl pb-8 sm:max-w-md sm:mx-auto"
        data-testid="directions-sheet"
      >
        <SheetHeader className="text-left">
          <SheetTitle>Open in</SheetTitle>
          <SheetDescription className="text-zinc-500 text-sm">
            {copyText}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-2">
          <a
            href={appleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={optionClass}
            data-testid="directions-apple-maps"
            onClick={() => onOpenChange(false)}
          >
            <Map className="w-5 h-5 text-zinc-700" />
            <span className="text-[15px] font-medium text-zinc-800">Apple Maps</span>
          </a>

          <a
            href={googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={optionClass}
            data-testid="directions-google-maps"
            onClick={() => onOpenChange(false)}
          >
            <MapPin className="w-5 h-5 text-zinc-700" />
            <span className="text-[15px] font-medium text-zinc-800">Google Maps</span>
          </a>

          <a
            href={wazeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={optionClass}
            data-testid="directions-waze"
            onClick={() => onOpenChange(false)}
          >
            <Navigation className="w-5 h-5 text-zinc-700" />
            <span className="text-[15px] font-medium text-zinc-800">Waze</span>
          </a>

          <button
            type="button"
            onClick={handleCopy}
            className={optionClass}
            data-testid="directions-copy-address"
          >
            <Copy className="w-5 h-5 text-zinc-700" />
            <span className="text-[15px] font-medium text-zinc-800">Copy Address</span>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DirectionsSheet;
